import * as React from 'react';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormField from '../../../../core/components/FormField.tsx';
import TagsField from '../../../../core/components/Fields/TagsField.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import { SmartInboxConfigStepProps } from '../SmartInboxConfigSteps.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';

const SpinnerIcon = withIcon(faSpinnerThird);

const UnprocessedDocumentsFormFields: React.FC<
    SmartInboxConfigStepProps & { showKeywordFields?: boolean; showMoveToFolderField?: boolean }
> = ({ form, destinationFolders = [], isLoading, showKeywordFields = true, showMoveToFolderField = true }) => {
    const { t } = useTranslation('integrations');

    const folderOptions = useMemo(
        () =>
            destinationFolders.map((folder) => ({
                value: folder.id,
                label: folder.name,
            })),
        [destinationFolders]
    );

    return (
        <div className="flex flex-col gap-6">
            {isLoading && (
                <div className="w-full h-full flex justify-center items-center">
                    <SpinnerIcon spin className="text-3xl text-content-weak" />
                </div>
            )}
            {!isLoading && (
                <>
                    {showKeywordFields && (
                        <>
                            <Controller
                                name="productConfigs.SMART_INBOX.actionConfigs.DOCUMENT_CLASSIFIED_POST_CLASSIFY.actionConfig.manual_processing_keywords"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('smartInbox.form.unprocessedDocumentsConfig.manualProcessingKeywords')}
                                    >
                                        <TagsField
                                            value={field.value || []}
                                            onValueChange={field.onChange}
                                            onBlur={field.onBlur}
                                            placeholder={t(
                                                'smartInbox.form.unprocessedDocumentsConfig.keywordsPlaceholder'
                                            )}
                                        />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="productConfigs.SMART_INBOX.actionConfigs.DOCUMENT_CLASSIFIED_POST_CLASSIFY.actionConfig.manual_processing_sender_emails"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t(
                                            'smartInbox.form.unprocessedDocumentsConfig.manualProcessingSenderEmails'
                                        )}
                                    >
                                        <TagsField
                                            value={field.value || []}
                                            onValueChange={field.onChange}
                                            onBlur={field.onBlur}
                                            placeholder={t(
                                                'smartInbox.form.unprocessedDocumentsConfig.senderEmailsPlaceholder'
                                            )}
                                        />
                                    </FormField>
                                )}
                            />
                        </>
                    )}

                    {showMoveToFolderField && (
                        <Controller
                            name="productConfigs.SMART_INBOX.actionConfigs.DOCUMENT_UNMATCHED_MOVE_TO_FOLDER.actionConfig"
                            control={form.control}
                            render={({ field }) => {
                                const currentFolderId = Object.values(field.value || {})[0] as string;
                                return (
                                    <FormField label={t('smartInbox.form.productConfig.actions.moveToFolder')}>
                                        <div onWheel={(e) => e.stopPropagation()}>
                                            <SelectField
                                                value={currentFolderId}
                                                options={folderOptions}
                                                renderOption={(option) => option.label}
                                                placeholder={t(
                                                    'smartInbox.form.productConfig.actions.movingActionPlaceHolder'
                                                )}
                                                onValueChange={(folderId) => {
                                                    field.onChange({ OTHER: folderId });
                                                }}
                                            />
                                        </div>
                                    </FormField>
                                );
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default UnprocessedDocumentsFormFields;
